import { CommonModule } from "@angular/common";
import { Component, DestroyRef, OnInit, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { catchError, forkJoin, of } from "rxjs";
import { RoleService } from "@app/shared/services/roles.service";
import {
  EMPTY_POST_HEARING_FILTERS,
  PostHearingTask,
  PostHearingTaskDisplayError,
  PostHearingTaskFilters,
  WorkflowDecisionRecord,
  canCompleteStep,
  formatAssignmentPosture,
  formatSubject,
  formatWorkflowStatus,
  isWorkflowCompleted,
  mapPostHearingError,
} from "./post-hearing-task.model";
import { PostHearingTaskService } from "./post-hearing-task.service";
import { PhmsCaseLifecycleComponent } from "./lifecycle/phms-case-lifecycle.component";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PHMS_HEARING_TERMINOLOGY } from "../phms-hearing.terminology";
import { PHMS_OPERATIONAL_TERMINOLOGY } from "../phms-operational.terminology";

/**
 * Post-hearing task tracking surface (story 967). Lists post-hearing workflow tasks from the
 * worklist, lets staff claim and complete required steps through the workflow runtime, and refreshes
 * from the server after each action. A failed command preserves the last confirmed server state and
 * never records a false completed status.
 */
@Component({
  selector: "phms-post-hearing-tasks",
  standalone: true,
  imports: [CommonModule, FormsModule, PhmsCaseLifecycleComponent],
  templateUrl: "./post-hearing-tasks.component.html",
  styleUrl: "./post-hearing-tasks.component.scss",
})
export class PostHearingTasksComponent implements OnInit {
  readonly hearingText = createTerminologyUsageSignals(PHMS_HEARING_TERMINOLOGY.postHearingTasks);
  readonly operationalText = createTerminologyUsageSignals(PHMS_OPERATIONAL_TERMINOLOGY.postHearingTasks);
  private readonly destroyRef = inject(DestroyRef);
  private readonly taskService = inject(PostHearingTaskService);
  private readonly roleService = inject(RoleService);
  private readonly router = inject(Router);

  readonly isLoading = signal(false);
  readonly isActing = signal(false);
  readonly errorState = signal<PostHearingTaskDisplayError | null>(null);
  readonly tasks = signal<PostHearingTask[]>([]);
  readonly pendingLegalReviews = signal<Record<number, WorkflowDecisionRecord>>({});
  readonly pendingPostHearingApprovals = signal<Record<number, WorkflowDecisionRecord>>({});
  readonly filters = signal<PostHearingTaskFilters>({
    ...EMPTY_POST_HEARING_FILTERS,
  });

  readonly hasTasks = computed(() => this.tasks().length > 0);
  readonly canReviewGrant = this.roleService.hasAnyRoleSignal("legal");
  readonly canApprovePostHearing = this.roleService.hasAnyRoleSignal("operations_manager");

  filterFormValues: PostHearingTaskFilters = { ...EMPTY_POST_HEARING_FILTERS };

  readonly pageTitle = this.hearingText.title;

  readonly isWorkflowCompleted = isWorkflowCompleted;
  readonly canCompleteStep = canCompleteStep;
  readonly formatWorkflowStatus = formatWorkflowStatus;
  readonly formatAssignmentPosture = formatAssignmentPosture;
  readonly formatSubject = formatSubject;

  ngOnInit(): void {
    this.load();
  }

  applyFilters(): void {
    this.filters.set({ ...this.filterFormValues });
    this.load();
  }

  refresh(): void {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorState.set(null);
    forkJoin({
      taskPage: this.taskService.getTasks(this.filters().status),
      legalReviewPage: this.taskService.getPendingLegalReviews(),
      postHearingApprovalPage: this.taskService.getPendingPostHearingApprovals(),
    })
      .pipe(
        catchError((error: unknown) => {
          // Preserve the last confirmed server state; only surface the error.
          this.errorState.set(mapPostHearingError(error));
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result) {
          this.tasks.set(result.taskPage.items ?? []);
          this.pendingLegalReviews.set(
            Object.fromEntries(
              (result.legalReviewPage.items ?? [])
                .filter((review) => typeof review.workflowInstanceId === "number")
                .map((review) => [review.workflowInstanceId as number, review]),
            ),
          );
          this.pendingPostHearingApprovals.set(
            Object.fromEntries(
              (result.postHearingApprovalPage.items ?? [])
                .filter((approval) => typeof approval.workflowInstanceId === "number")
                .map((approval) => [approval.workflowInstanceId as number, approval]),
            ),
          );
        }
        this.isLoading.set(false);
      });
  }

  claim(task: PostHearingTask): void {
    if (this.isActing() || isWorkflowCompleted(task)) {
      return;
    }
    this.runAction(this.taskService.claim(task.workflowInstanceId, task.workflowETag));
  }

  complete(task: PostHearingTask): void {
    if (
      this.isActing() ||
      !canCompleteStep(task) ||
      !task.currentStepCode ||
      this.pendingLegalReview(task) ||
      this.pendingPostHearingApproval(task)
    ) {
      return;
    }
    this.runAction(this.taskService.completeStep(task.workflowInstanceId, task.currentStepCode, task.stepETag));
  }

  openLegalReview(task: PostHearingTask): void {
    const review = this.pendingLegalReview(task);
    if (this.isActing() || !this.canReviewGrant() || !review) {
      return;
    }
    void this.router.navigate(["/phms/legal-reviews", review.id], {
      state: {
        phmsReturn: {
          url: this.router.url,
          label: this.pageTitle(),
        },
      },
    });
  }

  approvePostHearingCompletion(task: PostHearingTask): void {
    const approval = this.pendingPostHearingApproval(task);
    if (this.isActing() || !this.canApprovePostHearing() || !approval || !task.stepETag) {
      return;
    }
    this.runAction(this.taskService.approvePostHearingCompletion(approval.id, task.stepETag));
  }

  pendingLegalReview(task: PostHearingTask): WorkflowDecisionRecord | null {
    return this.pendingLegalReviews()[task.workflowInstanceId] ?? null;
  }

  pendingPostHearingApproval(task: PostHearingTask): WorkflowDecisionRecord | null {
    return this.pendingPostHearingApprovals()[task.workflowInstanceId] ?? null;
  }

  private runAction(action$: ReturnType<PostHearingTaskService["claim"]>): void {
    this.isActing.set(true);
    this.errorState.set(null);
    action$
      .pipe(
        catchError((error: unknown) => {
          // A failed command preserves the last confirmed server state and shows the failure.
          this.errorState.set(mapPostHearingError(error));
          return of<{ failed: true } | null>({ failed: true });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((outcome) => {
        this.isActing.set(false);
        // Refresh the worklist after a successful action so the next task / completed state shows.
        if (!(outcome && (outcome as { failed?: boolean }).failed)) {
          this.load();
        }
      });
  }

  trackByTask(_index: number, task: PostHearingTask): number {
    return task.workflowInstanceId;
  }
}
